import React from 'react';
import { Box, Card, Typography, Avatar, Chip, IconButton, Collapse } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, MoreVert, Warning } from '@mui/icons-material';
import OKRScoreRing from './OKRScoreRing';
import OKRProgressBar from './OKRProgressBar';

interface OKRCardProps {
    title: string;
    description?: string; // or team_context
    score: number;
    ownerName?: string;
    ownerAvatar?: string; // URL
    status?: 'GREEN' | 'YELLOW' | 'RED'; // Explicit status if available, otherwise derived from score
    lastUpdated?: string;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    onCheckIn?: () => void;
    children?: React.ReactNode; // For milestones or sub-items
    type?: 'ORG' | 'TEAM' | 'INDIVIDUAL';
}

const OKRCard: React.FC<OKRCardProps> = ({
    title,
    description,
    score,
    ownerName,
    ownerAvatar,
    status,
    lastUpdated,
    isExpanded,
    onToggleExpand,
    onCheckIn,
    children,
    type = 'INDIVIDUAL'
}) => {
    // Determine color accent based on type
    const accentColor = type === 'ORG' ? '#1976d2' : type === 'TEAM' ? '#7b1fa2' : '#ed6c02';

    return (
        <Card
            sx={{
                mb: 2,
                border: '1px solid #f0f0f0',
                boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 6px 16px rgba(0,0,0,0.08)'
                },
                borderRadius: 2,
                overflow: 'visible' // allow dropdowns or negative margins if needed
            }}
        >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Left Accent Bar */}
                <Box
                    sx={{
                        width: 4,
                        height: 40,
                        bgcolor: accentColor,
                        borderRadius: 2,
                        mt: 0.5
                    }}
                />

                {/* Main Content */}
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ maxWidth: '80%' }}>
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
                                {title}
                            </Typography>
                            {description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {description}
                                </Typography>
                            )}

                            {/* Metadata Line */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                                {ownerName && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar
                                            src={ownerAvatar}
                                            alt={ownerName}
                                            sx={{ width: 20, height: 20, fontSize: '0.75rem' }}
                                        >
                                            {ownerName.charAt(0)}
                                        </Avatar>
                                        <Typography variant="caption" color="text.secondary">
                                            {ownerName}
                                        </Typography>
                                    </Box>
                                )}
                                {lastUpdated && (
                                    <Typography variant="caption" color="text.secondary">
                                        Last updated: {lastUpdated}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Right Actions & Score */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            <OKRScoreRing score={score} size={50} showLabel={true} />

                            {/* Action Buttons */}
                            <Box sx={{ display: 'flex' }}>
                                {onCheckIn && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCheckIn(); }} title="Weekly Check-in">
                                        <Warning fontSize="small" color="action" />
                                    </IconButton>
                                )}
                                {onToggleExpand && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleExpand(); }} title="Expand Details">
                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Progress Bar (Visual reinforcement) */}
                    <Box sx={{ mt: 2, width: '100%' }}>
                        <OKRProgressBar score={score} showLabel={false} compact />
                    </Box>
                </Box>
            </Box>

            {/* Expanded Content (Children: Milestones/Tasks) */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{
                    bgcolor: '#fafafa',
                    borderTop: '1px solid #f0f0f0',
                    p: 2
                }}>
                    {children}
                </Box>
            </Collapse>
        </Card>
    );
};

export default OKRCard;
